import React, { useCallback, useMemo, useState } from 'react'
import type { EmployeeUserWiseRow } from '../../../types/employeeAnalysis'
import { ExportMenuButton } from '../../common/export-menu-button'
import {
  exportRowsToCsv,
  exportRowsToExcel,
  getUserWiseExportRows,
} from '../../../utils/employeeAnalysisExport'
import type { PeriodFilter } from '../../../utils/projectAnalysisPeriod'

export interface EmployeeUserWiseTableProps {
  baseRows: EmployeeUserWiseRow[]
  loading: boolean
  periodFilter: PeriodFilter
  onRowNavigate: (userId: number) => void
}

type SortKey =
  | 'total_hours'
  | 'billable_hours'
  | 'non_billable_hours'
  | 'utilization_pct'

export function EmployeeUserWiseTable({
  baseRows,
  loading,
  periodFilter,
  onRowNavigate,
}: EmployeeUserWiseTableProps) {
  const [sortBy, setSortBy] = useState<SortKey>('billable_hours')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [search, setSearch] = useState('')
  const [displayCount, setDisplayCount] = useState(20)

  const usersList = useMemo(() => {
    let list = [...baseRows]
    const term = search.toLowerCase().trim()
    if (term) {
      list = list.filter(
        (u) =>
          (u.full_name || '').toLowerCase().includes(term) ||
          (u.username || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term),
      )
    }
    const mult = sortOrder === 'asc' ? 1 : -1
    list.sort((a, b) => {
      const va = a[sortBy]
      const vb = b[sortBy]
      if (typeof va === 'number' && typeof vb === 'number')
        return mult * (va - vb)
      return mult * String(va || '').localeCompare(String(vb || ''))
    })
    return list
  }, [baseRows, search, sortBy, sortOrder])

  const displayedUsers = usersList.slice(0, displayCount)
  const remainingCount = Math.max(0, usersList.length - displayCount)

  const periodLabel =
    periodFilter.fy !== 'all'
      ? String(periodFilter.fy)
      : periodFilter.year !== 'all'
        ? String(periodFilter.year)
        : 'all'

  const onExportCsv = useCallback(() => {
    const rows = getUserWiseExportRows(usersList)
    exportRowsToCsv(`user_wise_hours_utilization_${periodLabel}`, rows)
  }, [usersList, periodLabel])

  const onExportExcel = useCallback(() => {
    const rows = getUserWiseExportRows(usersList)
    exportRowsToExcel(
      `user_wise_hours_utilization_${periodLabel}`,
      'User-wise Hours',
      rows,
    )
  }, [usersList, periodLabel])

  const Th = ({
    col,
    label,
  }: {
    col: SortKey
    label: string
  }) => (
    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:bg-slate-100">
      <button
        type="button"
        className="flex items-center gap-1"
        onClick={() => {
          if (sortBy === col) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
          } else {
            setSortBy(col)
            setSortOrder('desc')
          }
        }}
      >
        {label}
        {sortBy === col && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
      </button>
    </th>
  )

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center gap-2 text-slate-500 text-sm">
        <span className="inline-block h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        Loading user hours…
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-sm font-bold text-slate-900">
          User-wise hours & utilization
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <ExportMenuButton
            disabled={usersList.length === 0}
            onCsv={onExportCsv}
            onExcel={onExportExcel}
          />
          <input
            type="search"
            placeholder="Search name, username, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-48 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                User
              </th>
              <Th col="total_hours" label="Total hrs" />
              <Th col="billable_hours" label="Billable hrs" />
              <Th col="non_billable_hours" label="Non-bill. hrs" />
              <Th col="utilization_pct" label="Util. %" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {usersList.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-10 text-center text-sm text-slate-500"
                >
                  {baseRows.length > 0 ? (
                    <p>No employees match your search.</p>
                  ) : (
                    <div className="space-y-2">
                      <p>No employee timesheet data for this view.</p>
                      <p className="text-xs">
                        Run{' '}
                        <code className="bg-slate-100 px-1 rounded text-[11px]">
                          npm run generate-user-summary-stats
                        </code>{' '}
                        if needed.
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              displayedUsers.map((user) => (
                <tr
                  key={user.user_id}
                  onClick={() => onRowNavigate(user.user_id)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                          user.is_active ? 'bg-blue-500' : 'bg-slate-400'
                        }`}
                      >
                        {(user.full_name || '?').charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRowNavigate(user.user_id)
                          }}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 truncate text-left block max-w-[200px]"
                        >
                          {user.full_name || user.username || '—'}
                        </button>
                        <div className="text-[10px] text-slate-500 truncate">
                          @{user.username || '—'}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 tabular-nums">
                    {user.total_hours.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-emerald-700 tabular-nums">
                    {user.billable_hours.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-red-600 tabular-nums">
                    {user.non_billable_hours.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 tabular-nums">
                    {user.utilization_pct.toFixed(1)}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {remainingCount > 0 && (
        <div className="px-4 py-2 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setDisplayCount((c) => c + 20)}
            className="w-full py-1.5 rounded-lg text-sm bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            Load more ({remainingCount} remaining)
          </button>
        </div>
      )}
    </div>
  )
}
